import { useState, useEffect } from "react";
import Avvvatars from "avvvatars-react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Table from "../../core/common/dataTable/index";
import { ExternalNoteTable } from "../../core/data/interface"; //Les types des colones de notre tableau déclaré au préalable
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
// import EditSubjectModal from "./editSubjectModal";
import CreateExternalNoteModal from "./createExternalNoteModal";
import EditExternalNoteModal from "./editExternalNoteModal";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { deleteExternalNote, getAllExternalNote } from "../../api/externalNote";
import ExternalModalApercu from "./externalModalApercu";
import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";
import { my_routes } from "../router/all_routes";
// import TooltipOption from "./tooltipOption";

// bouton protégée pour ajouter une note externe
const ProtectedBtnAddExternalNote = withPermission(
  (props: any) => (
    <div className="mb-2">
      <button
        onClick={() => {
          props.setShowCreate(true);
        }}
        //   to="#"
        className="btn btn-primary d-flex align-items-center"
      >
        <i className="ti ti-square-rounded-plus me-2" />
        Nouvelle Note
      </button>
    </div>
  ),
  [buildPermission(Resource.EXTERNAL_NOTE, Action.CREATE)],
  "masquer",
);

// bouton protégée pour modifier une note externe
const ProtectedBtnUpdateExternalNote = withPermission(
  (props: any) => (
    <li>
      <Link
        className="dropdown-item rounded-1"
        to="#"
        onClick={() => {
          props.setEditNote(props.record.id);
          props.setShowEdit(true);
        }}
      >
        <i className="ti ti-edit-circle me-2" />
        Modifier
      </Link>
    </li>
  ),
  [buildPermission(Resource.EXTERNAL_NOTE, Action.UPDATE)],
  "masquer",
);

// bouton protégée pour supprimer une note externe
const ProtectedBtnDeleteExternalNote = withPermission(
  (props: any) => (
    <li>
      <Link
        onClick={() => props.showConfirmationAlert(props.record)}
        className="dropdown-item rounded-1"
        to="#"
      >
        <i className="ti ti-trash-x me-2" />
        Supprimer
      </Link>
    </li>
  ),
  [buildPermission(Resource.EXTERNAL_NOTE, Action.DELETE)],
  "masquer",
);

const ExternalNote = () => {
  const routes = my_routes;

  const { selectedYear } = useAcademicYear();

  //Initialisation des state utilisés
  const [allNotes, setAllNotes] = useState([]);
  const [editNote, setEditNote] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  // State pour le PDF et le modal releve de note
  const [showModal, setShowModal] = useState(false);
  const [urlPiece, setUrlPiece] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);
  //Initialisation de sweetAlert
  const MySwal = withReactContent(Swal);

  const getMention = (note: number): string => {
    const roundedNote = Math.round(note * 100) / 100; // Arrondi à 2 décimales
    if (roundedNote >= 0 && roundedNote < 10) return "Contrôle";
    if (roundedNote >= 10 && roundedNote < 12) return "Passable";
    if (roundedNote >= 12 && roundedNote < 14) return "Assez Bien";
    if (roundedNote >= 14 && roundedNote < 16) return "Bien";
    if (roundedNote >= 16 && roundedNote < 18) return "Très Bien";
    if (roundedNote >= 18 && roundedNote <= 20) return "Excellent";
    return "Non noté";
  };

  // Les colonnes de notre tableau
  const columns = [
    {
      title: "Année",
      dataIndex: "academicYear",
      sorter: (a: ExternalNoteTable, b: ExternalNoteTable) =>
        a.academicYear.length - b.academicYear.length,
      render: (text: string, record: any) => (
        <p>{record.Level.AcademicYear.label}</p>
      ),
    },
    {
      title: "Étudiant",
      dataIndex: "matricule",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <Link
            to={`/student-detail/${record.id}`}
            className="avatar avatar-md"
          >
            <Avvvatars
              value={`${record.StudentProfil.firstname?.charAt(0) || ""}${
                record.StudentProfil.lastname?.charAt(0) || ""
              }`}
              style="character" // ou "character"
            />
          </Link>
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link
                to={`/student-detail/${record.id_studentProfil}`}
              >{`${record.StudentProfil.firstname} ${record.StudentProfil.lastname}`}</Link>
            </p>
            <span className="fs-12">{record.StudentProfil.matricule}</span>
          </div>
        </div>
      ),
      sorter: (a: ExternalNoteTable, b: ExternalNoteTable) =>
        a.matricule.length - b.matricule.length,
    },
    {
      title: "Établissement",
      dataIndex: "university",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link
                to={`/student-detail/${record.id}`}
              >{`${record.university}`}</Link>
            </p>
            <span className="fs-12">
              {record.matricule} - {record.classe}
            </span>
          </div>
        </div>
      ),
      sorter: (a: ExternalNoteTable, b: ExternalNoteTable) =>
        a.university.length - b.university.length,
    },
    {
      title: "Note",
      dataIndex: "average",
      sorter: (a: ExternalNoteTable, b: ExternalNoteTable) =>
        a.average.length - b.average.length,
      render: (text: string, record: any) => {
        const note = Number(record.average);
        const formattedNote = note.toFixed(2); // Formatage à 2 décimales
        return (
          <div>
            <span style={{ fontWeight: "bold" }}>{formattedNote}</span>
            <span
              style={{ fontSize: "0.8em", color: "#666", marginLeft: "8px" }}
            >
              ({getMention(note)})
            </span>
          </div>
        );
      },
    },
    {
      title: "Piece",
      dataIndex: "pieceJustificatif",
      render: (text: string, record: any) => (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip-top">Pièce Justificatif</Tooltip>}
        >
          <button
            type="button"
            className="btn btn-outline-light bg-white btn-icon me-1"
            onClick={() => {
              setUrlPiece(record.pieceJustificatif);
              setShowModal(true);
            }}
          >
            <i className="ti ti-paperclip" />
          </button>
        </OverlayTrigger>
      ),
    },
    {
      title: "Date de création",
      dataIndex: "createdAt",
      render: (date: string) => {
        const d = new Date(date);
        return d.toLocaleDateString("fr-FR");
      },
      sorter: (a: ExternalNoteTable, b: ExternalNoteTable) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: any) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                {/* modifier note externe */}
                <ProtectedBtnUpdateExternalNote
                  setEditNote={setEditNote}
                  record={record}
                  setShowEdit={setShowEdit}
                />
                {/* supprimer note externe */}
                <ProtectedBtnDeleteExternalNote
                  showConfirmationAlert={showConfirmationAlert}
                  record={record}
                />
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  //Traitement en alert de la demande de suppression de salle de classe
  const showConfirmationAlert = (externalNote: any) => {
    MySwal.fire({
      title: "Supprimer une note externe ?",
      text: `Souhaitez-vous vraiment supprimer cette note externe ?`,
      showCancelButton: true,
      // confirmButtonColor: "#00ff00",
      confirmButtonText: "Envoyer!",
      // cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          //On lance la requête d'envoi de mail
          await deleteExternalNote(externalNote?.id);
          MySwal.fire({
            title: "Note externe supprimée avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });

          reloadData(); //On reload la liste
        } catch (error) {
          const err = error as AxiosError<any>;
          //On affiche une alerte en cas d'erreur côté serveur
          MySwal.fire({
            title: "Erreur",
            text:
              err.message ||
              err.response?.data?.message ||
              err.response?.data?.error,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  //Reload de la liste
  const reloadData = () => setRefreshKey((prev) => prev + 1);

  //On recupère la liste des Salles de classe en appelant sa fonction
  useEffect(() => {
    const fetchData = async () => {
      // On attend que selectedYear soit défini
      if (!selectedYear?.id) return;

      setHasLoaded(false);
      setIsLoading(true);

      try {
        const notes = await getAllExternalNote();
        // console.log(notes);
        setAllNotes(notes.data);
      } catch (error: any) {
        // Si erreur (Toast Error)
        toast.error(
          error.message || "Une erreur s'est produit, Réessayer plus tard",
        );
      } finally {
        setIsLoading(false); //Les données ne sont plus en chargement
        setHasLoaded(true); //Les données ont été chargé avec succès
      }
    };

    fetchData();
  }, [refreshKey, selectedYear]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Gestion des Notes Externes</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.allNotes}>Notes</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Notes Externes
                  </li>
                </ol>
              </nav>
            </div>
            {/* Boutton en entête de la page */}
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <ProtectedBtnAddExternalNote setShowCreate={setShowCreate} />
            </div>
          </div>
          {/* /Page Header */}

          {/* Tableau liste du personnel administratif */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Tableau des Notes</h4>
            </div>
            <div className="card-body p-0 py-3">
              {/* Staffs List (On affiche le tableau qui si les données son chargées même vide) */}
              {isLoading && !hasLoaded ? (
                <div className="text-center">
                  <div className="spinner-border" role="status"></div>
                  <span className="visually">Chargement des Données...</span>
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={allNotes}
                  Selection={true}
                />
              )}
              {/* /Staffs List */}
            </div>
          </div>
        </div>
        {/* /Page Header */}

        {/* <EditSubjectModal show={showEdit} subjectID={editSubject} onClose={() => setShowEdit(false)} onSaved={reloadData} /> */}
        <CreateExternalNoteModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSaved={reloadData}
        />
        <EditExternalNoteModal
          show={showEdit}
          externalNoteID={editNote}
          onClose={() => setShowEdit(false)}
          onSaved={reloadData}
        />

        <ExternalModalApercu
          show={showModal}
          metaData={{
            urlPiece,
            academicYear: selectedYear?.label,
          }}
          onClose={() => setShowModal(false)}
        />
      </div>
    </>
  );
};

export default ExternalNote;
